import {get} from 'lodash';

import type {RunFieldsFragment} from '../../../generated/graphql';
import {MAX_PINNED_RUNS} from '../../../util/constants';
import {type GroupSelections} from '../groupSelections/types';
import type {ViewReducerState} from '../reducerSupport';
import type {Ref, RunSetPart} from './types';

/**
 * Retrieves a runset out of the view state.
 *
 * @param state - The current view reducer state containing all parts
 * @param ref - A reference object containing the ID of the runset to retrieve
 * @returns The RunSetPart if found, or null if the runset doesn't exist
 */
export function getRunSet(
  state: ViewReducerState,
  ref: Ref
): RunSetPart | null {
  return get(state, ['parts', 'runSet', ref.id], null);
}

/**
 * Retrieves the groupSelections for a runset out of the view state.
 *
 * Looks up the runset first, then uses its groupSelectionsRef to find
 * the associated group selections configuration.
 *
 * @param state - The current view reducer state containing all parts
 * @param ref - A reference object containing the ID of the runset
 * @returns The GroupSelections if found, or null if the runset or group
 *          selections don't exist
 */
export function getGroupSelections(
  state: ViewReducerState,
  ref: Ref
): GroupSelections | null {
  const runSet = getRunSet(state, ref);
  if (!runSet) {
    return null;
  }
  return get(
    state,
    ['parts', 'group-selections', runSet.groupSelectionsRef.id],
    null
  );
}

/** Minimal run object interface requiring only an id property */
type RunLike = {id: string};

/**
 * Sets or unsets the pinned state of a run within a runset.
 *
 * This function handles pinning/unpinning runs while maintaining
 * immutability. It returns the original runset reference if no changes are
 * needed (e.g., run is already in the desired state) to avoid unnecessary
 * re-renders.
 *
 * @param runSet - The runset to modify
 * @param run - The run to pin or unpin (only the id property is used)
 * @param pinned - Whether the run should be pinned (true) or unpinned (false)
 * @returns A new RunSetPart with updated pinnedRunIds, or the original
 *          runset if unchanged
 */
export function setRunPinned(
  runSet: RunSetPart,
  run: RunLike,
  pinned: boolean
): RunSetPart {
  const pinnedRunIds = new Set(runSet.pinnedRunIds);

  if (pinnedRunIds.has(run.id) && pinned) {
    // Run is already pinned, don't create new reference identities needlessly.
    return runSet;
  }
  if (!pinnedRunIds.has(run.id) && !pinned) {
    // Run is already unpinned, don't create new reference identities needlessly.
    return runSet;
  }

  if (pinned) {
    pinnedRunIds.add(run.id);
  } else {
    pinnedRunIds.delete(run.id);
  }

  const runSetCopy: RunSetPart = {...runSet};

  // If unpinning the baseline run, also clear the baselineRunId:
  if (runSet.baselineRunId === run.id && !pinned) {
    delete runSetCopy.baselineRunId;
  }

  runSetCopy.pinnedRunIds = Array.from(pinnedRunIds);
  return runSetCopy;
}

/**
 * Sets or clears the baseline run for a runset.
 *
 * The baseline run is used as a reference point for comparison with other
 * runs. This function maintains immutability by returning the original
 * runset reference if the baseline is already set to the specified run.
 *
 * @param runSet - The runset to modify
 * @param run - The run to set as baseline, or null to clear the baseline
 * @returns A new RunSetPart with updated baselineRunId, or the original
 *          runset if unchanged
 */
export function setRunBaseline(runSet: RunSetPart, run: RunLike | null) {
  if (run && runSet.baselineRunId === run.id) {
    // Baseline is already set to this run, don't create new reference
    // identities needlessly.
    return runSet;
  }
  if (!run && !runSet.baselineRunId) {
    // Baseline is already unset, don't create new reference identities
    // needlessly.
    return runSet;
  }

  const runSetCopy: RunSetPart = {...runSet};

  if (!run) {
    runSetCopy.baselineRunId = undefined;
    return runSetCopy;
  }

  runSetCopy.baselineRunId = run.id;
  return runSetCopy;
}

/**
 * Sets a run as both pinned and baseline in a single operation.
 *
 * This is a convenience function that combines `setRunBaseline` and
 * `setRunPinned`. When a run is set as baseline, it should also be pinned
 * to ensure it remains visible for comparison. If null is passed, only the
 * baseline is cleared (the run remains pinned if it was previously pinned).
 *
 * @param runSet - The runset to modify
 * @param run - The run to set as pinned and baseline, or null to clear only
 *              the baseline
 * @returns A new RunSetPart with updated baselineRunId and pinnedRunIds
 */
export function setRunPinnedAndBaseline(
  runSet: RunSetPart,
  run: RunLike | null
) {
  if (run) {
    return setRunPinned(setRunBaseline(runSet, run), run, true);
  } else {
    return setRunBaseline(runSet, null);
  }
}

/**
 * Checks whether additional runs can be pinned to the runset.
 *
 * There is a maximum limit on the number of pinned runs (defined by
 * MAX_PINNED_RUNS). This function checks if the current number of pinned
 * runs is below that limit.
 *
 * @param runSet - The runset to check
 * @returns True if more runs can be pinned, false if the limit has been
 *          reached or exceeded.
 */
export function canPinRun(runSet: RunSetPart): boolean {
  return Boolean(
    !runSet.pinnedRunIds || runSet.pinnedRunIds.length < MAX_PINNED_RUNS
  );
}

/**
 * Updates a runSet by applying a transformation function to the existing
 * runSet.
 *
 * This function looks up the runset by reference, applies the provided
 * transformation function, and returns a new state with the updated runset.
 * It will return original state if:
 * - The runset cannot be found
 * - The transformation function returns the same reference identity
 * - The transformation function throws an error
 *
 * @param state - The current view reducer state
 * @param ref - A Ref of the runset to update
 * @param fn - A transformation function that receives the runset and a
 *             boolean indicating whether grouping is active, and returns
 *             the updated runset
 * @returns The updated state with the modified runset, or the original
 *          state if unchanged
 */
export function updateRunSet(
  state: ViewReducerState,
  ref: Ref,
  fn: (runSet: RunSetPart, isGrouping: boolean) => RunSetPart
) {
  const runSet = getRunSet(state, ref);
  if (!runSet) {
    return state;
  }
  const groupSelections = getGroupSelections(state, ref);

  try {
    const updatedRunSet = fn(runSet, Boolean(groupSelections?.grouping.length));
    if (updatedRunSet === runSet) {
      // returned the same reference identity, don't update state:
      return state;
    }

    return {
      ...state,
      parts: {
        ...state.parts,
        runSet: {
          ...state.parts.runSet,
          [ref.id]: updatedRunSet,
        },
      },
    };
  } catch (e) {
    console.log('Error updating runSet:', e);
    return state;
  }
}

/**
 * Cleans up stale pinned runs and baseline references from a runset.
 *
 * This function validates that the pinned runs and baseline run stored in
 * the runset still exist in the actual list of project runs. Runs may become
 * stale if they are moved or deleted from the project. The cleanup is
 * skipped when grouping is active, because pinned runs won't exist in the
 * provided array.
 *
 * @param runSet - The runset to clean up
 * @param pinnedRuns - The current list of valid pinned runs from the server.
 *                     Note that "pinned" here also includes the baseline run.
 * @param isGrouping - Whether grouping is currently active
 * @returns A cleaned up runset with invalid references removed.
 */
export function removeMissingPinnedRunIds(
  runSet: RunSetPart,
  pinnedRuns: RunFieldsFragment[],
  isGrouping: boolean
) {
  // If a grouping is active, don't do cleanup.
  if (isGrouping) {
    return runSet;
  }
  const pinnedRunIds = new Set(pinnedRuns.map(r => r.id));
  const newRunSet = {...runSet};

  // Baseline run has been moved or deleted from this project, so remove
  // it from the runset baseline run:
  if (newRunSet.baselineRunId && !pinnedRunIds.has(newRunSet.baselineRunId)) {
    delete newRunSet.baselineRunId;
  }
  // Filter pinned runs to only those that still exist in the project:
  if (newRunSet.pinnedRunIds) {
    newRunSet.pinnedRunIds = newRunSet.pinnedRunIds.filter(id =>
      pinnedRunIds.has(id)
    );
  }

  return newRunSet;
}

/**
 * Ensures that the baseline run is included in the pinned runs of a runset.
 *
 * This is necessary because originally baseline runs were not automatically
 * pinnied, but that assumption changed later. This function probably isn't
 * necessary forever.
 *
 * @param runSet - the runset to fix up
 * @returns A new RunSetPart with the baseline run included in pinnedRunIds.
 */
export function fixupRunsetBaselineRuns(runSet: RunSetPart) {
  const pinnedRunIdsSet = new Set(runSet.pinnedRunIds);

  if (runSet.baselineRunId && !pinnedRunIdsSet.has(runSet.baselineRunId)) {
    pinnedRunIdsSet.add(runSet.baselineRunId);
  }

  const newRunSet = {...runSet};
  newRunSet.pinnedRunIds = Array.from(pinnedRunIdsSet);
  return newRunSet;
}

/**
 * Checks if a run is pinned in the runset and returns true if it is.
 *
 * @param pinnedRunIds
 * @param run
 */
export function isRunPinned(
  pinnedRunIds: string[] | undefined,
  run: RunLike
): boolean {
  return pinnedRunIds ? pinnedRunIds.includes(run.id) : false;
}
